import AudienceSwitch from "work/proposals/comments/_view/audience-switch";
import Comment from "work/proposals/comments/comment";
import Proposal from "work/proposals/proposal";
import reaimCommentAtAudience from "work/proposals/comments/reaim-comment-at-audience";

type CommentAudienceSwitchProps = {
  proposal: Proposal;
  /** The comment as the editing row's draft currently aims it, which is not yet what the store holds. */
  comment: Comment;
  /** Hands back the comment re-aimed at the chosen audience, for the row to hold until it is saved. */
  onCommentReaimed: (comment: Comment) => void;
};

/**
 * The audience switch for a comment that already exists — the control an author meets while editing a
 * row, as opposed to the composer's switch, which chooses the audience of a comment not yet written.
 *
 * Widening is one-way. An internal comment can still be re-aimed at the counterparty, which re-homes it
 * on the external thread (hence the cleared thread id); what the counterparty has already read cannot be
 * pulled back inside the author's party, so an external comment's switch is disabled. An author who
 * widened by mistake cancels the edit rather than narrowing it back — nothing has been sent until they
 * save.
 *
 * The re-aimed comment is handed back rather than applied here. A comment is immutable and its thread is
 * shared with the rest of the conversation, so the new audience lives in the editing row's draft, and
 * reaches the store — and therefore the audience a Save reads — only when the author saves.
 *
 * Shared by the archive's comment list and the live proposal's Comments pane so the rule is
 * stated once — the two surfaces render the same comment rows and must offer the same choice.
 */
export default function CommentAudienceSwitch({
  proposal,
  comment,
  onCommentReaimed,
}: Readonly<CommentAudienceSwitchProps>) {
  return (
    <AudienceSwitch
      proposal={proposal}
      disabled={Boolean(comment.id && comment.thread.isExternal)}
      isExternal={comment.thread.isExternal}
      onIsExternalChanged={(nextIsExternal) => onCommentReaimed(reaimCommentAtAudience(comment, nextIsExternal))}
    />
  );
}
